/**
 * je-experiments.js — client-side A/B experiment assignment for Journey's End Games.
 *
 * Loaded on all Shopify theme pages via theme.liquid, after je-analytics.js.
 * Exposes window.JEExperiments.
 *
 * Configuration:
 *   window.JE_EXPERIMENTS_ENDPOINT — GET target for the running-experiment
 *   config (set in je-analytics-init.liquid, derived from the analytics
 *   endpoint so preview deploys point at the right API automatically).
 *
 * How it works:
 *   1. Assignment runs SYNCHRONOUSLY at script parse time (in <head>, before
 *      any body content paints) from a fresh localStorage-cached config
 *      (repeat visitors) or the Liquid-embedded snapshot in
 *      snippets/je-experiments-config.liquid (window.JE_EXPERIMENTS_CONFIG,
 *      for zero-flicker first visits). The API config
 *      ({ key, traffic, variants[] }) is refreshed in the background on
 *      every page view (the endpoint's max-age=30 HTTP cache keeps that
 *      cheap) so a paused experiment propagates within ~a minute; a visitor
 *      with neither cache nor embedded config is assigned when the fetch
 *      returns (may paint control first).
 *   2. Variants are assigned deterministically by hashing
 *      (visitor_id, experiment key) — the same visitor always gets the same
 *      variant while the config is unchanged, with no server round-trip.
 *      Assignments are also persisted (je:exp:<key>) so mid-flight weight
 *      changes never reshuffle already-assigned visitors.
 *   3. Enrolled assignments are applied to <html> as
 *      class="je-exp-<key>--<variant>" and data-je-exp-<key>="<variant>"
 *      so CSS-only experiments need no extra JS.
 *   4. Exposure is explicit: call JEExperiments.expose(key) when the tested
 *      UI is shown, or put data-je-experiment="<key>" on the tested element
 *      to auto-expose via IntersectionObserver when it becomes visible.
 *      Exposure emits one experiment.exposed event per experiment per page
 *      load, and all subsequent analytics batches carry the exposed map so
 *      page_visits.experiments stays populated.
 *
 * Kill switch: pausing/completing an experiment removes it from the config;
 * getVariant() then returns null and the storefront falls back to control UI.
 */
(function () {
  "use strict";

  var CONFIG_CACHE_KEY = "je:experiments-config";
  var CONFIG_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
  var ASSIGNMENT_KEY_PREFIX = "je:exp:";
  var PREVIEW_KEY = "je:exp:preview";
  var TOKEN_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

  // { key, traffic, variants: [{key, weight}] } by experiment key
  var _experiments = null; // null until config is known (cache or fetch)
  var _assignments = {}; // experiment key -> variant (enrolled only)
  var _exposed = {}; // experiment key -> variant, exposed this page load
  var _forced = readPreviewOverrides(); // experiment key -> forced variant
  var _readyCallbacks = [];
  var _ready = false;

  function readPreviewOverrides() {
    var overrides = {};
    try {
      var raw = sessionStorage.getItem(PREVIEW_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        for (var storedKey in parsed) {
          if (TOKEN_RE.test(storedKey) && TOKEN_RE.test(parsed[storedKey] || "")) {
            overrides[storedKey] = parsed[storedKey];
          }
        }
      }

      var value = new URLSearchParams(window.location.search).get("je-preview");
      if (value) {
        var separator = value.indexOf(":");
        var expKey = separator === -1 ? "" : value.slice(0, separator);
        var variant = separator === -1 ? "" : value.slice(separator + 1);
        if (TOKEN_RE.test(expKey)) {
          if (variant === "off") delete overrides[expKey];
          else if (TOKEN_RE.test(variant)) overrides[expKey] = variant;
          sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(overrides));
        }
      }
    } catch (_) {
      // sessionStorage or URL parsing unavailable — preview is best-effort
    }
    return overrides;
  }

  // ---------------------------------------------------------------------------
  // Deterministic hashing (FNV-1a 32-bit)
  // ---------------------------------------------------------------------------

  function fnv1a(str) {
    var hash = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      // hash *= 16777619, in 32-bit space
      hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
    }
    return hash >>> 0;
  }

  function bucket(seed, modulo) {
    return fnv1a(seed) % modulo;
  }

  // ---------------------------------------------------------------------------
  // Config cache
  // ---------------------------------------------------------------------------

  function readCachedConfig() {
    try {
      var raw = localStorage.getItem(CONFIG_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.fetched_at !== "number" ||
          !isFinite(parsed.fetched_at) || !Array.isArray(parsed.experiments)) return null;
      var age = Date.now() - parsed.fetched_at;
      if (age < 0 || age > CONFIG_CACHE_TTL_MS) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function writeCachedConfig(experiments) {
    try {
      localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify({
        fetched_at: Date.now(),
        experiments: experiments
      }));
    } catch (_) {
      // localStorage unavailable — config just refetches next load
    }
  }

  function validExperiment(exp) {
    if (!exp || !TOKEN_RE.test(exp.key || "")) return false;
    if (typeof exp.traffic !== "number" || exp.traffic < 0 || exp.traffic > 100) return false;
    if (!Array.isArray(exp.variants) || exp.variants.length < 2) return false;
    for (var i = 0; i < exp.variants.length; i++) {
      var v = exp.variants[i];
      if (!v || !TOKEN_RE.test(v.key || "")) return false;
      if (typeof v.weight !== "number" || v.weight < 1) return false;
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Assignment
  // ---------------------------------------------------------------------------

  function getVisitorId() {
    if (window.JEAnalytics && window.JEAnalytics.getVisitorId) {
      return window.JEAnalytics.getVisitorId();
    }
    return null;
  }

  function readStoredAssignment(expKey) {
    try {
      return localStorage.getItem(ASSIGNMENT_KEY_PREFIX + expKey);
    } catch (_) {
      return null;
    }
  }

  function storeAssignment(expKey, variant) {
    try {
      localStorage.setItem(ASSIGNMENT_KEY_PREFIX + expKey, variant);
    } catch (_) {
      // best-effort — deterministic hashing keeps it stable anyway
    }
  }

  function computeAssignment(exp, visitorId) {
    // Enrollment gate: bucket 0-99 must fall under the traffic percentage.
    if (bucket("enroll:" + exp.key + ":" + visitorId, 100) >= exp.traffic) {
      return null;
    }
    // Variant selection by cumulative weight (decorrelated from enrollment).
    var totalWeight = 0;
    for (var i = 0; i < exp.variants.length; i++) totalWeight += exp.variants[i].weight;
    var point = bucket("variant:" + exp.key + ":" + visitorId, totalWeight);
    var cumulative = 0;
    for (var j = 0; j < exp.variants.length; j++) {
      cumulative += exp.variants[j].weight;
      if (point < cumulative) return exp.variants[j].key;
    }
    return exp.variants[exp.variants.length - 1].key;
  }

  function variantExists(exp, variant) {
    for (var i = 0; i < exp.variants.length; i++) {
      if (exp.variants[i].key === variant) return true;
    }
    return false;
  }

  function assignAll(experiments) {
    var visitorId = getVisitorId();
    _experiments = {};
    _assignments = {};
    for (var forcedKey in _forced) {
      _assignments[forcedKey] = _forced[forcedKey];
      applyDomMarkers(forcedKey, _forced[forcedKey]);
    }
    if (!visitorId) {
      // No stable identity (storage blocked) — never enroll, control UI only.
      finishReady();
      return;
    }
    for (var i = 0; i < experiments.length; i++) {
      var exp = experiments[i];
      if (!validExperiment(exp)) continue;
      _experiments[exp.key] = exp;
      if (_forced[exp.key]) continue;

      // Sticky: a previously stored assignment wins while its variant still
      // exists, so weight changes only affect not-yet-assigned visitors.
      var variant = readStoredAssignment(exp.key);
      if (!variant || !variantExists(exp, variant)) {
        variant = computeAssignment(exp, visitorId);
        if (variant) storeAssignment(exp.key, variant);
      }
      if (variant) {
        _assignments[exp.key] = variant;
        applyDomMarkers(exp.key, variant);
      }
    }
    finishReady();
  }

  function applyDomMarkers(expKey, variant) {
    try {
      var root = document.documentElement;
      root.classList.add("je-exp-" + expKey + "--" + variant);
      root.setAttribute("data-je-exp-" + expKey, variant);
    } catch (_) {
      // never block the storefront
    }
  }

  function finishReady() {
    _ready = true;
    var callbacks = _readyCallbacks.splice(0, _readyCallbacks.length);
    for (var i = 0; i < callbacks.length; i++) {
      try {
        callbacks[i](api);
      } catch (_) {
        // user callback errors must not break the loop
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Config loading
  // ---------------------------------------------------------------------------

  var _assigned = false;

  // Assignment happens at most once per page load: a mid-page config swap
  // could otherwise flip already-rendered UI.
  function assignOnce(experiments) {
    if (_assigned) return;
    _assigned = true;
    assignAll(experiments);
  }

  function fetchConfig() {
    var endpoint = window.JE_EXPERIMENTS_ENDPOINT;
    if (!endpoint || typeof fetch !== "function") {
      assignOnce([]);
      return;
    }

    fetch(endpoint, { method: "GET" })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        if (body && Array.isArray(body.experiments)) {
          writeCachedConfig(body.experiments);
          assignOnce(body.experiments);
        } else {
          // Non-OK / malformed response: keep any existing cache intact.
          assignOnce([]);
        }
      })
      .catch(function () {
        assignOnce([]);
      });
  }

  // ---------------------------------------------------------------------------
  // Exposure
  // ---------------------------------------------------------------------------

  function expose(expKey) {
    var variant = _assignments[expKey] || null;
    if (!variant) return null;
    if (_forced[expKey]) return variant;
    if (_exposed[expKey]) return variant;
    _exposed[expKey] = variant;

    if (window.JEAnalytics && window.JEAnalytics.track) {
      window.JEAnalytics.track("experiment.exposed", {
        experiment: expKey,
        variant: variant
      });
    }
    return variant;
  }

  function getExposed() {
    var keys = Object.keys(_exposed);
    if (keys.length === 0) return null;
    var out = {};
    for (var i = 0; i < keys.length; i++) out[keys[i]] = _exposed[keys[i]];
    return out;
  }

  // Auto-exposure for declarative experiments: any element with
  // data-je-experiment="<key>" is exposed when ~30% visible.
  function initAutoExposure() {
    if (typeof IntersectionObserver !== "function") return;
    var elements = document.querySelectorAll("[data-je-experiment]");
    if (elements.length === 0) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var key = entry.target.getAttribute("data-je-experiment");
        if (key) expose(key);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.3 });

    elements.forEach(function (el) { observer.observe(el); });
  }

  function observe(element, expKey) {
    if (!element || !_assignments[expKey]) return;
    if (typeof IntersectionObserver !== "function") {
      expose(expKey);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        expose(expKey);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.3 });
    observer.observe(element);
  }

  function ready(callback) {
    if (typeof callback !== "function") return;
    if (_ready) {
      try {
        callback(api);
      } catch (_) {
        // never block the storefront
      }
    } else {
      _readyCallbacks.push(callback);
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  var api = {
    /** Assigned variant for an experiment, or null when not enrolled /
     *  not running / config not loaded yet. Does NOT record exposure. */
    getVariant: function (expKey) {
      return _assignments[expKey] || null;
    },
    /** All current assignments: { experiment_key: variant }. */
    getAssignments: function () {
      var out = {};
      for (var k in _assignments) out[k] = _assignments[k];
      return out;
    },
    /** Record exposure (once per page load) and return the variant. */
    expose: expose,
    /** Expose when the given element becomes visible. */
    observe: observe,
    /** Experiments exposed on this page load: { key: variant } or null.
     *  je-analytics.js attaches this to every batch envelope. */
    getExposed: getExposed,
    /** Run a callback once assignment is complete (immediately if done). */
    ready: ready
  };

  window.JEExperiments = api;

  // Synchronous first-paint assignment: this script executes in <head>
  // before any body content renders, so assigning here means the <html>
  // markers exist before first paint — no flicker.
  //
  // Config source priority:
  //   1. Valid localStorage cache (repeat visitors) — authoritative even when
  //      empty so a background refresh can stop an embedded experiment after
  //      it is paused/completed; used for up to 24 hours and refreshed below.
  //   2. window.JE_EXPERIMENTS_CONFIG (Liquid-embedded snapshot, rendered
  //      before this script) — used only when no valid cache exists, providing
  //      zero-flicker assignment for brand-new visitors when non-empty.
  //   3. Neither (or an empty config): assignment waits for the background
  //      fetch (first paint may show control — fine for below-the-fold or
  //      JS-driven experiments).
  var cachedConfig = readCachedConfig();
  var embedded = window.JE_EXPERIMENTS_CONFIG;
  if (cachedConfig) {
    // A valid cache — even an empty one — is the API's latest word on what's
    // running, so it always outranks the embedded snapshot. An empty cache
    // assigns nothing here; the background fetch below settles it.
    if (cachedConfig.experiments.length > 0) assignOnce(cachedConfig.experiments);
    else if (Object.keys(_forced).length > 0) assignOnce([]);
  } else if (embedded && Array.isArray(embedded.experiments) && embedded.experiments.length > 0) {
    assignOnce(embedded.experiments);
  } else if (Object.keys(_forced).length > 0) {
    // Draft experiments are intentionally absent from the public config, but
    // a forced preview must still become ready synchronously.
    assignOnce([]);
  }

  // Background refresh on every page view (deferred one tick so
  // je-analytics-init.liquid, which sets JE_EXPERIMENTS_ENDPOINT, has run).
  // When already assigned this just rewrites the cache for the next page
  // view, so a paused/completed experiment (the kill switch) propagates
  // within ~a minute instead of lingering in a "fresh" localStorage cache.
  // The endpoint's Cache-Control (max-age=30, s-maxage=60) keeps repeated
  // fetches cheap.
  setTimeout(fetchConfig, 0);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      ready(initAutoExposure);
    });
  } else {
    ready(initAutoExposure);
  }
})();
